import { Observer } from 'mobx-react-lite';
import numeral from 'numeral';
import type { JSX } from 'react';
import React from 'react';
import { Link } from 'react-router-dom';

import { CampaignClass, ECampaignClass } from '@feathr/blackbox';
import type { IColumn } from '@feathr/components';
import { Skeleton, Text } from '@feathr/components';
import CampaignTypeChip from '@feathr/extender/components/CampaignTypeChip';
import { useLocalUrl, useStore } from '@feathr/extender/state';

import type { ITransformedCampaignData } from './EmailLimits.types';

import * as tableStyles from '@feathr/components/dist/Table/Table.css';

// Helper function to map ECampaignClass to CampaignClass
function mapToCampaignClass(type: ECampaignClass): CampaignClass {
  const mapping: Record<ECampaignClass, CampaignClass> = {
    [ECampaignClass.retargeting]: CampaignClass.Segment,
    [ECampaignClass.mobileGeoFencing]: CampaignClass.MobileGeoFencing,
    [ECampaignClass.mobileGeoFenceRetargeting]: CampaignClass.MobileGeoFenceRetargeting,
    [ECampaignClass.facebook]: CampaignClass.Facebook,
    [ECampaignClass.emailListFacebook]: CampaignClass.EmailListFacebook,
    [ECampaignClass.emailList]: CampaignClass.EmailList,
    [ECampaignClass.search]: CampaignClass.Search,
    [ECampaignClass.seedSegment]: CampaignClass.SeedSegment,
    [ECampaignClass.affinity]: CampaignClass.Affinity,
    [ECampaignClass.googleAdsSmart]: CampaignClass.GoogleAdsSmart,
    [ECampaignClass.trackedLink]: CampaignClass.TrackedLink,
    [ECampaignClass.landingPage]: CampaignClass.LandingPage,
    [ECampaignClass.singleSend]: CampaignClass.PinpointEmail,
    [ECampaignClass.smartSend]: CampaignClass.SmartPinpointEmail,
    [ECampaignClass.autoSend]: CampaignClass.AutoPinpointEmail,
    [ECampaignClass.drip]: CampaignClass.Drip,
    [ECampaignClass.mailrailAutoSend]: CampaignClass.MailrailAutoSend,
    [ECampaignClass.mailrailSingleSend]: CampaignClass.MailrailSingleSend,
    [ECampaignClass.conversation]: CampaignClass.Conversation,
    [ECampaignClass.invites]: CampaignClass.Referral,
    [ECampaignClass.omni]: CampaignClass.Omni,
  };
  return mapping[type];
}

export function campaignBreakdownColumns(
  totalEmailsSent: number,
): Array<IColumn<ITransformedCampaignData>> {
  return [
    {
      accessor: 'name',
      Header: 'Campaign Name',
      Footer: () => <Text.DefaultMedium>Page totals</Text.DefaultMedium>,
    },
    {
      accessor: 'project',
      Header: 'Project',
      Cell: ({ value }): JSX.Element => {
        return (
          <Observer>
            {function useAnonymousFunction(): JSX.Element {
              const localUrl = useLocalUrl();
              const { Events } = useStore();
              const projectId = value;
              const project = Events.get(projectId);

              if (project.isPending) {
                return <Skeleton />;
              }
              const to = localUrl(project.getItemUrl());
              return <Link to={to}>{project.get('name')}</Link>;
            }}
          </Observer>
        );
      },
      Footer: '',
    },
    {
      accessor: 'type',
      Header: 'Type',
      className: tableStyles.cellCenter,
      Cell: ({ value }): JSX.Element => {
        const campaignClass = mapToCampaignClass(value as ECampaignClass);
        return <CampaignTypeChip cls={campaignClass} />;
      },
      Footer: '',
    },
    {
      accessor: 'humanStartDate',
      Header: 'Start Date',
      Cell: ({ value }) => <span>{value}</span>,
      Footer: '',
    },
    {
      accessor: 'humanEndDate',
      Header: 'End Date',
      Cell: ({ value }) => <span>{value}</span>,
      Footer: '',
    },
    {
      accessor: 'humanEmailsSent',
      Header: 'Emails Sent',
      className: tableStyles.cellRight,
      footerClassName: tableStyles.cellRight,
      Cell: ({ value }) => <span>{numeral(value).format('0,0')}</span>,
      Footer: () => (
        <Text.DefaultMedium>{numeral(totalEmailsSent).format('0,0')}</Text.DefaultMedium>
      ),
    },
  ];
}
